// Air quality (uHoo CO2) and ventilation level helpers shared by the Energy and Vent tabs.
import { EnergyAirQuality, EnergyPlanSnapshot, VENT_LEVELS, VentLevel } from '../../common';
import { DOT } from '../components/ui/tokens';

const SLOT_MS = 15 * 60_000;
const AIR_STALE_AFTER_MS = 30 * 60_000;

export const VENT_NAMES: Record<VentLevel, string> = { away: 'Away', low: 'Low', middle: 'Middle', high: 'High' };
// low / mid / high = 1..3 on the charts' level axis (away = 0 is never planned).
export const ventIndex = (level: VentLevel | undefined) => (level === undefined ? null : VENT_LEVELS.indexOf(level));

export function co2Status(ppm: number, t: EnergyAirQuality['thresholds']): { label: string; dot: string } {
  return ppm < t.good
    ? { label: 'Good', dot: DOT.good }
    : ppm <= t.high
      ? { label: 'Elevated', dot: DOT.warning }
      : { label: 'High', dot: DOT.critical };
}

export interface AirRoom {
  name: string;
  co2: number;
  status: string;
  dot: string;
}

// The latest CO2 per uHoo room with its status ("stale" after 30 min without a reading).
export function airRooms(plan: EnergyPlanSnapshot | undefined, now: number): AirRoom[] {
  const air = plan?.airQuality;
  if (!air) {
    return [];
  }
  return air.rooms
    .filter((r) => r.latest?.co2 !== undefined)
    .map((r) => {
      const co2 = Math.round(r.latest!.co2!);
      if (now - r.latest!.timestamp > AIR_STALE_AFTER_MS) {
        return { name: r.name, co2, status: 'stale', dot: DOT.off };
      }
      const s = co2Status(co2, air.thresholds);
      return { name: r.name, co2, status: s.label, dot: s.dot };
    });
}

// The level running now: the plan's in live mode, otherwise the last measured one.
export function ventLevelNow(plan: EnergyPlanSnapshot, now: number): VentLevel | undefined {
  const planned = (plan.slots.find((s) => s.start <= now && now < s.start + SLOT_MS) ?? plan.slots[0])?.ventLevel;
  const measured = [...(plan.actual ?? [])].reverse().find((a) => a.ventLevel)?.ventLevel;
  return plan.ventilationMode === 'live' ? (planned ?? measured) : (measured ?? planned);
}
